import type { QueryClient } from '@tanstack/react-query'
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	Scripts,
} from '@tanstack/react-router'
import { X } from 'lucide-react'
import { closeSnackbar, SnackbarProvider } from 'notistack'
import type { useAuth } from 'react-oidc-context'
import {
	DefaultErrorSnackbar,
	DefaultSnackbar,
	ErrorSnackbar,
	InfoSnackbar,
	SuccessSnackbar,
	WarningSnackbar,
} from '@/components/customSnackbar'
import { ErrorComponent } from '@/components/ErrorComponent'
import { LoadingComponent } from '@/components/LoadingComponent'
import type { UserSessionValue } from '@/context/user-session'
import type { AppConfig } from '@/hooks/use-appConfig'
import type { AppAbility } from '@/integrations/casl/ability'
import { TitleFromBreadcrumbs } from '@/shared/TitleFromBreadcrumbs'

function NotFoundComponent() {
	return <div>404: Not Found</div>
}

function SnackbarCloseAction({
	snackbarId,
}: Readonly<{
	snackbarId: string | number
}>) {
	return (
		<button
			type="button"
			onClick={() => closeSnackbar(snackbarId)}
			style={{
				border: 'none',
				background: 'transparent',
				cursor: 'pointer',
				color: 'white',
			}}
		>
			<X size={16} />
		</button>
	)
}

function renderSnackbarAction(snackbarId: string | number) {
	return <SnackbarCloseAction snackbarId={snackbarId} />
}

export interface RouterContext {
	queryClient: QueryClient
	auth: ReturnType<typeof useAuth>
	config: AppConfig
	ability: AppAbility
	userSession: UserSessionValue
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootDocument,
	pendingComponent: LoadingComponent,
	errorComponent: ErrorComponent,
	notFoundComponent: NotFoundComponent,
})

function RootDocument() {
	return (
		<SnackbarProvider
			Components={{
				apiError: ErrorSnackbar,
				error: DefaultErrorSnackbar,
				success: SuccessSnackbar,
				warning: WarningSnackbar,
				info: InfoSnackbar,
				default: DefaultSnackbar,
			}}
			maxSnack={5}
			hideIconVariant={false}
			preventDuplicate={false}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			autoHideDuration={5000}
			action={renderSnackbarAction}
		>
			<HeadContent />
			<TitleFromBreadcrumbs />
			<Outlet />
			<Scripts />
		</SnackbarProvider>
	)
}
